import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/hooks/useAuth';
import { clearImpersonationToken } from '@/lib/auth';

/**
 * Sticky top banner that appears whenever the active session is an
 * impersonation (i.e. the JWT carries imp=true). Makes it impossible
 * for an admin to forget they're "viewing as" a customer when staring
 * at the customer's data.
 *
 * The "Exit" button clears this tab's sessionStorage and closes the
 * window. window.close only works for tabs opened by JS (which is the
 * case here — the reseller portal opens this tab via window.open) so
 * the close call is safe.
 */
export function ImpersonationBanner(): JSX.Element | null {
  const { t } = useTranslation('default');
  const { user } = useAuth();

  if (!user?.impersonating) return null;

  const onExit = () => {
    clearImpersonationToken();
    // window.close only works on tabs opened by JS — which this one is.
    // If the browser blocks it (rare), fall back to the login page so
    // the operator at least sees they exited.
    window.close();
    window.location.href = '/login';
  };

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-3 px-4 py-2 bg-warn-bg border-b border-warn-bd text-[13px] text-warn"
    >
      <span aria-hidden="true" className="w-2 h-2 rounded-full bg-warn animate-pulse" />
      <span className="font-medium">
        {t('impersonation_banner', { customer: user.name, by: user.actingEmail ?? '' })}
      </span>
      <button
        type="button"
        onClick={onExit}
        className="ml-2 px-2.5 py-1 bg-surface text-text border border-line rounded-md text-[12px] font-medium hover:bg-surface-2 transition-colors"
      >
        {t('impersonation_exit')}
      </button>
    </div>
  );
}
